import { useRef, useState, useEffect, KeyboardEvent, ClipboardEvent } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  TextField,
  Link,
  alpha,
  useTheme,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AnimateButton from '@/components/ui/AnimateButton';

const OTP_LENGTH = 4;

interface OtpStepProps {
  phone: string;
  onVerify: (otp: string) => void;
  onResend: () => void;
  error: string;
  loading: boolean;
  resendCooldown: number; // seconds remaining before resend is allowed
  permissionDenied?: boolean;
}

export default function OtpStep({ phone, onVerify, onResend, error, loading, resendCooldown, permissionDenied = false }: OtpStepProps) {
  const theme = useTheme();
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (permissionDenied) return;
    if (digits.every((d) => d !== '')) {
      onVerify(digits.join(''));
    }
  }, [digits]);

  const handleChange = (idx: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[idx] = digit;
    setDigits(next);
    if (digit && idx < OTP_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const maskedPhone = phone.length >= 10
    ? `+91 XXXXXX${phone.slice(-4)}`
    : phone;

  return (
    <Box>
      {/* Icon */}
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '16px',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        <LockOutlinedIcon sx={{ color: 'primary.main', fontSize: 28 }} />
      </Box>

      <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
        Enter OTP
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        We sent a 4-digit code to{' '}
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {maskedPhone}
        </Box>
      </Typography>

      {/* 6-box OTP input */}
      <Stack direction="row" gap={1.25} justifyContent="center" sx={{ mb: 3 }} onPaste={handlePaste}>
        {digits.map((digit, idx) => (
          <TextField
            key={idx}
            id={`otp-box-${idx}`}
            inputRef={(el) => { inputRefs.current[idx] = el; }}
            value={digit}
            onChange={(e) => handleChange(idx, e.target.value)}
            onKeyDown={(e) => handleKeyDown(idx, e)}
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: 'center',
                fontSize: '1.375rem',
                fontWeight: 700,
                padding: '10px 0',
                width: 44,
              },
            }}
            error={!!error}
            sx={{
              width: 52,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: digit
                  ? alpha(theme.palette.primary.main, 0.06)
                  : 'background.paper',
                '& fieldset': {
                  borderColor: digit
                    ? theme.palette.primary.main
                    : 'rgba(145, 158, 171, 0.48)',
                  borderWidth: digit ? 2 : 1.5,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                },
              },
            }}
          />
        ))}
      </Stack>

      {/* Error message */}
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* Verify button */}
      <AnimateButton>
        <Button
          id="verify-otp-btn"
          fullWidth
          variant="contained"
          size="large"
          disabled={permissionDenied || digits.some((d) => !d) || loading}
          onClick={() => onVerify(digits.join(''))}
          sx={{ py: 1.5, fontSize: '0.9375rem', borderRadius: '10px', mb: 2.5 }}
        >
          {loading ? 'Verifying…' : 'Verify OTP'}
        </Button>
      </AnimateButton>

      {/* Resend */}
      <Typography variant="body2" textAlign="center" color="text.secondary">
        Didn't receive the code?{' '}
        {permissionDenied || resendCooldown > 0 ? (
          <Box component="span" sx={{ color: 'text.disabled', cursor: 'not-allowed' }}>
            {permissionDenied
              ? 'Resend OTP'
              : `Resend in ${resendCooldown}s`}
          </Box>
        ) : (
          <Link
            id="resend-otp-link"
            component="button"
            type="button"
            onClick={onResend}
            underline="hover"
            sx={{ fontWeight: 600, cursor: 'pointer' }}
          >
            Resend OTP
          </Link>
        )}
      </Typography>
    </Box>
  );
}
